import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LLM_MODELS, type LLMModelId } from "@/services/api";

interface LLMSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (model: LLMModelId) => void;
  title?: string;
}

export default function LLMSelectionModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Select LLM Model",
}: LLMSelectionModalProps) {
  const [selectedModel, setSelectedModel] = useState<LLMModelId>("claude-opus-4-7");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl max-w-sm w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#063F6B] to-[#124C77] p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10 min-w-[40px] min-h-[40px] flex items-center justify-center"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-4">
            Choose the AI model to use for this evaluation:
          </p>

          <div className="space-y-2 mb-5">
            {LLM_MODELS.map((model) => (
              <label
                key={model.id}
                className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedModel === model.id
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/30"
                }`}
              >
                <input
                  type="radio"
                  name="llm-model"
                  value={model.id}
                  checked={selectedModel === model.id}
                  onChange={() => setSelectedModel(model.id)}
                  className="sr-only"
                />
                <div
                  className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedModel === model.id
                      ? "border-primary"
                      : "border-muted-foreground/40"
                  }`}
                >
                  {selectedModel === model.id && (
                    <div className="w-2 h-2 rounded-full bg-primary" />
                  )}
                </div>
                <div>
                  <span className="font-medium text-foreground">{model.name}</span>
                  <span className="text-xs text-muted-foreground ml-2 capitalize">
                    {model.provider}
                  </span>
                </div>
              </label>
            ))}
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="brand"
              className="flex-1"
              onClick={() => onConfirm(selectedModel)}
            >
              Run Evaluation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
